import { getDoc, setDoc } from './store';
import type { Cuidado, DiaRelogio, Docs, Item, Refeicao, Suplemento, Treino, Unidade } from './types';
import { addDays, parseYmd, today, uid } from '../lib/date';
import { N_, t } from '../lib/i18n';

type D = Docs['bemestar'];
const mut = (fn: (d: D) => D) => setDoc('bemestar', fn);

export const REFEICOES_PADRAO: [string, string, string][] = [
  [N_('Café da manhã'), '☕', '07:00'], [N_('Lanche da manhã'), '🍎', '10:00'], [N_('Almoço'), '🍽️', '12:30'],
  [N_('Lanche da tarde'), '🥪', '16:00'], [N_('Jantar'), '🍲', '19:30'], [N_('Ceia'), '🥛', '22:00'],
];
export const CUIDADOS_PADRAO: { nome: string; icon: string; hora: string | null; passos: [string, number][] }[] = [
  { nome: N_('Skincare da manhã'), icon: '🧴', hora: '07:15', passos: [[N_('Lavar o rosto com sabonete facial'), 1], [N_('Tônico'), 1], [N_('Hidratante'), 1], [N_('Protetor solar'), 1]] },
  { nome: N_('Skincare da noite'), icon: '🌙', hora: '22:00', passos: [[N_('Demaquilante ou água micelar'), 1], [N_('Sabonete facial'), 1], [N_('Sérum'), 1], [N_('Hidratante'), 1]] },
  { nome: N_('Alongamento'), icon: '🤸', hora: null, passos: [[N_('Pescoço: inclinar para os lados'), 1], [N_('Ombros: girar para trás'), 1], [N_('Braços acima da cabeça'), 1], [N_('Tocar os pés'), 1], [N_('Respirar fundo 5 vezes'), 1]] },
  { nome: N_('Higiene bucal'), icon: '🪥', hora: null, passos: [[N_('Fio dental'), 1], [N_('Escovar os dentes'), 2], [N_('Enxaguante'), 1]] },
  { nome: N_('Cabelo'), icon: '💇', hora: null, passos: [[N_('Shampoo'), 2], [N_('Condicionador'), 3], [N_('Finalizador'), 1]] },
];
export const TODOS_DIAS = [0, 1, 2, 3, 4, 5, 6];

export function salvarRefeicao(r: Refeicao) {
  mut(d => ({ ...d, refeicoes: d.refeicoes.some(x => x.id === r.id) ? d.refeicoes.map(x => (x.id === r.id ? r : x)) : [...d.refeicoes, r] }));
}
export const excluirRefeicao = (id: string) => mut(d => ({ ...d, refeicoes: d.refeicoes.filter(x => x.id !== id) }));

export function salvarCuidado(c: Cuidado) {
  mut(d => ({ ...d, cuidados: d.cuidados.some(x => x.id === c.id) ? d.cuidados.map(x => (x.id === c.id ? c : x)) : [...d.cuidados, c] }));
}
export const excluirCuidado = (id: string) => mut(d => ({ ...d, cuidados: d.cuidados.filter(x => x.id !== id) }));

/** O nome vem de REFEICOES_PADRAO (marcado com N_) e é traduzido aqui, ao criar. */
export const novaRefeicao = (nome = '', icon = '🍽️', hora = '12:00'): Refeicao => ({ id: uid(), nome: nome ? t(nome) : '', icon, hora, itens: [], dias: TODOS_DIAS });
export const novoCuidado = (p?: (typeof CUIDADOS_PADRAO)[number]): Cuidado => ({
  id: uid(), nome: p ? t(p.nome) : '', icon: p?.icon ?? '✨', hora: p?.hora ?? null, dias: TODOS_DIAS,
  passos: (p?.passos ?? []).map(([texto, min]) => ({ id: uid(), texto: t(texto), min })),
});

/** Marca ou desmarca um item (refeição ou cuidado) como feito no dia. */
export function alternarFeito(id: string, data = today(), valor?: boolean) {
  mut(d => {
    const cur = d.feitos[data] ?? [];
    const tem = cur.includes(id);
    const quer = valor ?? !tem;
    if (quer === tem) return d;
    const feitos = { ...d.feitos, [data]: quer ? [...cur, id] : cur.filter(x => x !== id) };
    // guarda só os últimos 60 dias
    const lim = addDays(today(), -60);
    for (const k of Object.keys(feitos)) if (k < lim) delete feitos[k];
    return { ...d, feitos };
  });
}

/** Copos do dia, cada um com o seu volume em ml (antes da 0.43 era só a contagem). */
export const coposDo = (d: D, data = today()) => d.aguaCopos?.[data] ?? Array.from({ length: d.agua[data] ?? 0 }, () => d.copoMl ?? 250);

function gravarCopos(d: D, data: string, copos: number[]): D {
  const lim = addDays(today(), -90);
  const ag = { ...d.agua, [data]: copos.length };
  const ac = { ...(d.aguaCopos ?? {}), [data]: copos };
  for (const k of Object.keys(ag)) if (k < lim) delete ag[k];
  for (const k of Object.keys(ac)) if (k < lim) delete ac[k];
  return { ...d, agua: ag, aguaCopos: ac };
}
/** + um copo (do tamanho padrão ou do volume informado) ou − o último. */
export function agua(delta: number, data = today(), ml?: number) {
  mut(d => {
    const c = [...coposDo(d, data)];
    if (delta > 0) for (let i = 0; i < delta; i++) c.push(ml ?? d.copoMl ?? 250);
    else for (let i = 0; i < -delta && c.length; i++) c.pop();
    return gravarCopos(d, data, c);
  });
}
/** Troca o volume de um copo já registrado (ex.: era 200 ml, foi 400 ml). ml 0 tira o copo. */
export function mudarCopo(i: number, ml: number, data = today()) {
  mut(d => {
    const c = [...coposDo(d, data)];
    if (i < 0 || i >= c.length) return d;
    if (ml <= 0) c.splice(i, 1); else c[i] = Math.round(ml);
    return gravarCopos(d, data, c);
  });
}
export const totalAgua = (d: D, data = today()) => coposDo(d, data).reduce((s, x) => s + x, 0);
export const metaAgua = (n: number) => mut(d => { const c = Math.max(1, Math.min(20, n)); return { ...d, metaAgua: c, ...(d.metaMl ? { metaMl: c * (d.copoMl ?? 250) } : {}) }; });

export const doDia = <T extends { dias: number[] }>(xs: T[], data = today()) => xs.filter(x => x.dias.includes(parseYmd(data).getDay()));

/** Dias seguidos (até hoje) em que o item foi feito. */
export function sequencia(id: string) {
  const f = getDoc('bemestar').feitos;
  let n = 0, d = today();
  if (!(f[d] ?? []).includes(id)) d = addDays(d, -1);
  while ((f[d] ?? []).includes(id)) { n++; d = addDays(d, -1); }
  return n;
}

// ---------- Relógio e apps de saúde (0.33) ----------
/** Junta os dias lidos (ou digitados) com os que já estão guardados. Mantém 90 dias. */
export function guardarDias(lidos: Record<string, DiaRelogio>) {
  const lim = addDays(today(), -90);
  setDoc('bemestar', b => {
    const relogio = { ...(b.relogio ?? {}) };
    for (const [d, x] of Object.entries(lidos)) {
      const antes = relogio[d];
      // o que a pessoa digitou à mão continua valendo onde o relógio não trouxe nada
      const limpo = Object.fromEntries(Object.entries(x).filter(([, v]) => v !== undefined && v !== 0)) as DiaRelogio;
      relogio[d] = { ...(antes?.fonte === 'manual' ? antes : {}), ...limpo, fonte: x.fonte } as DiaRelogio;
    }
    for (const k of Object.keys(relogio)) if (k < lim) delete relogio[k];
    return { ...b, relogio };
  });
}

/** Digitar à mão (sem relógio, no site ou para corrigir). */
export function registrarAMao(d: string, x: Partial<DiaRelogio>) {
  const atual = getDoc('bemestar').relogio?.[d];
  setDoc('bemestar', b => ({ ...b, relogio: { ...(b.relogio ?? {}), [d]: { ...(atual ?? {}), ...x, fonte: atual?.fonte && atual.fonte !== 'manual' ? atual.fonte : 'manual', em: new Date().toISOString() } } }));
}

export const metaPassos = (n: number) => mut(d => ({ ...d, metaPassos: Math.max(1000, Math.min(50000, n)) }));

// ---------- 0.34: seções, ordem, água em ml, alterações, exercício, suplementação ----------
export function alternarSecao(id: string) {
  mut(d => { const r = d.recolhidas ?? []; return { ...d, recolhidas: r.includes(id) ? r.filter(x => x !== id) : [...r, id] }; });
}
export const ordenarRefeicoes = (l: Refeicao[]) => mut(d => ({ ...d, refeicoes: l, refManual: true }));
export const ordenarCuidados = (l: Cuidado[]) => mut(d => ({ ...d, cuidados: l }));
export const ordenarTreinos = (l: Treino[]) => mut(d => ({ ...d, treinos: l }));
export const ordenarSuplementos = (l: Suplemento[]) => mut(d => ({ ...d, suplementos: l }));
/** Refeições na ordem do cronograma: pelo horário até a pessoa arrastar; depois, a ordem dela. */
export const refeicoesEmOrdem = (d: D) => (d.refManual ? d.refeicoes : [...d.refeicoes].sort((a, b) => a.hora.localeCompare(b.hora)));

export function salvarTreino(x: Treino) {
  mut(d => { const l = d.treinos ?? []; return { ...d, treinos: l.some(y => y.id === x.id) ? l.map(y => (y.id === x.id ? x : y)) : [...l, x] }; });
}
export const excluirTreino = (id: string) => mut(d => ({ ...d, treinos: (d.treinos ?? []).filter(x => x.id !== id) }));
export function salvarSuplemento(x: Suplemento) {
  mut(d => { const l = d.suplementos ?? []; return { ...d, suplementos: l.some(y => y.id === x.id) ? l.map(y => (y.id === x.id ? x : y)) : [...l, x] }; });
}
export const excluirSuplemento = (id: string) => mut(d => ({ ...d, suplementos: (d.suplementos ?? []).filter(x => x.id !== id) }));

/** Meta de água em ml e tamanho do copo; a meta em copos acompanha. */
export function aguaMl(metaMl: number, copoMl: number) {
  const m = Math.max(250, Math.min(6000, Math.round(metaMl))), c = Math.max(50, Math.min(1000, Math.round(copoMl)));
  mut(d => ({ ...d, metaMl: m, copoMl: c, metaAgua: Math.max(1, Math.ceil(m / c)) }));
}

/** O que foi diferente do cronograma neste dia (null apaga). */
export function alteracao(refId: string, data: string, alt: { texto: string; itens?: Item[] } | null) {
  mut(d => ({ ...d, refeicoes: d.refeicoes.map(r => {
    if (r.id !== refId) return r;
    const a = { ...(r.alt ?? {}) };
    if (!alt || (!alt.texto.trim() && !alt.itens)) delete a[data]; else a[data] = alt;
    const lim = addDays(today(), -90);
    for (const k of Object.keys(a)) if (k < lim) delete a[k];
    return { ...r, alt: a };
  }) }));
}

/** Alimentos que a pessoa mais usa (do cronograma e das alterações), com a última quantidade. */
export function maisUsados(d: D): { nome: string; qtd?: number; un?: Unidade; n: number }[] {
  const m = new Map<string, { nome: string; qtd?: number; un?: Unidade; n: number }>();
  const add = (i: Item, peso: number) => {
    const k = i.text.trim().toLowerCase(); if (!k) return;
    const x = m.get(k) ?? { nome: i.text.trim(), n: 0 };
    x.n += peso; if (i.qtd) { x.qtd = i.qtd; x.un = i.un; }
    m.set(k, x);
  };
  for (const r of d.refeicoes) {
    for (const i of r.itens) add(i, Math.max(1, r.dias.length));
    for (const a of Object.values(r.alt ?? {})) for (const i of a.itens ?? []) add(i, 1);
  }
  return [...m.values()].sort((a, b) => b.n - a.n);
}

/** Relatório da semana (segunda a domingo): o planejado e o que foi comido, e os totais por alimento. */
export function semanaAlimentar(d: D, seg: string) {
  const dias = Array.from({ length: 7 }, (_, i) => addDays(seg, i));
  const hoje = today();
  const linhas: { data: string; ref: Refeicao; feita: boolean; planejado: Item[]; comido: Item[]; texto: string; mudou: boolean }[] = [];
  const tot = new Map<string, { nome: string; un: string; plan: number; com: number }>();
  const soma = (i: Item, campo: 'plan' | 'com') => {
    const un = i.un ?? 'un'; const k = i.text.trim().toLowerCase() + '|' + un;
    const x = tot.get(k) ?? { nome: i.text.trim(), un, plan: 0, com: 0 };
    x[campo] += i.qtd ?? 1; tot.set(k, x);
  };
  for (const data of dias) {
    const dow = parseYmd(data).getDay();
    for (const r of refeicoesEmOrdem(d)) {
      if (!r.dias.includes(dow)) continue;
      const alt = r.alt?.[data];
      const feita = (d.feitos[data] ?? []).includes(r.id);
      const comido = alt?.itens ?? (feita ? r.itens : []);
      linhas.push({ data, ref: r, feita, planejado: r.itens, comido, texto: alt?.texto ?? '', mudou: !!alt });
      r.itens.forEach(i => soma(i, 'plan'));
      if (data <= hoje) comido.forEach(i => soma(i, 'com'));
    }
  }
  return { dias, linhas, totais: [...tot.values()].sort((a, b) => b.com - a.com || b.plan - a.plan) };
}

// ---------- 0.43: cardápio por data, exercícios concluídos, frequência ----------
/** O que comer numa data: o planejado só para ela ou, se não houver, o cardápio da semana. */
export const planoDoDia = (r: Refeicao, data: string) => r.plano?.[data] ?? r.itens;
/** Planeja (ou volta ao padrão, com null) o cardápio de uma refeição numa data. */
export function planejarDia(refId: string, data: string, itens: Item[] | null) {
  mut(d => ({ ...d, refeicoes: d.refeicoes.map(r => {
    if (r.id !== refId) return r;
    const p = { ...(r.plano ?? {}) };
    if (itens) p[data] = itens; else delete p[data];
    const lim = addDays(today(), -90);
    for (const k of Object.keys(p)) if (k < lim) delete p[k];
    return { ...r, plano: p };
  }) }));
}
export const chaveEx = (treino: string, ex: string) => `${treino}:${ex}`;
export function alternarEx(treino: string, ex: string, data = today()) {
  mut(d => {
    const k = chaveEx(treino, ex);
    const cur = d.feitosEx?.[data] ?? [];
    const prox = cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k];
    const fe = { ...(d.feitosEx ?? {}), [data]: prox };
    const lim = addDays(today(), -90);
    for (const x of Object.keys(fe)) if (x < lim) delete fe[x];
    // treino inteiro concluído: marca o treino como feito no dia
    const tr = d.treinos?.find(x => x.id === treino);
    const tudo = !!tr && tr.exercicios.length > 0 && tr.exercicios.every(e => prox.includes(chaveEx(treino, e.id)));
    const f = d.feitos[data] ?? [];
    const feitos = tudo && !f.includes(treino) ? { ...d.feitos, [data]: [...f, treino] } : !tudo && f.includes(treino) && cur.length > prox.length ? { ...d.feitos, [data]: f.filter(x => x !== treino) } : d.feitos;
    return { ...d, feitosEx: fe, feitos };
  });
}
/** Feito / previsto de um item (pelos dias da semana dele) nos últimos n dias, contando hoje. */
export function frequencia(d: D, id: string, dias: number[], n: number) {
  let previstos = 0, feitos = 0;
  for (let i = 0; i < n; i++) {
    const data = addDays(today(), -i);
    if (!dias.includes(parseYmd(data).getDay())) continue;
    previstos++;
    if ((d.feitos[data] ?? []).includes(id)) feitos++;
  }
  return { previstos, feitos };
}

/** Cópia de refeição, cuidado, treino ou suplemento, logo abaixo do original (0.52). */
export function duplicarBE(campo: 'refeicoes' | 'cuidados' | 'treinos' | 'suplementos', id: string) {
  mut(d => {
    const l = ((d[campo] ?? []) as { id: string; nome: string }[]);
    const i = l.findIndex(x => x.id === id); if (i < 0) return d;
    const o = JSON.parse(JSON.stringify(l[i]));
    const novo = { ...o, id: uid(), nome: t('{titulo} (cópia)', { titulo: o.nome }) };
    for (const k of ['itens', 'passos', 'exercicios']) if (Array.isArray(novo[k])) novo[k] = novo[k].map((x: { id: string }) => ({ ...x, id: uid() }));
    const c = [...l]; c.splice(i + 1, 0, novo);
    return { ...d, [campo]: c };
  });
}
